import type { ICustomTheme } from '@engyalo/delivery-ui-components/lib/interfaces/theme'

/**
 * The palette cinnamon paints with before the SCS config resolves (COM-1110).
 *
 * `AppTheme` falls back to the UI library's own `YaloLightPalette` when it is
 * handed nothing, so the pre-config paint used to be Yalo purple `#6E42B2` —
 * a brand, just not the shopper's. The ticket called it "default MUI blue";
 * no code path in `@engyalo/delivery-ui-components@1.23.0` produces `#1976d2`,
 * so look for purple rather than blue when checking this by hand.
 * `components/tests/splashBootColour.test.tsx` measures it rather than
 * asserting it from here: remove the merge below and that suite reports
 * `#6e42b2` off the real spinner.
 *
 * `primary` is the library's `grey.900` rather than a mid grey, and the reason
 * is the scrim. The splash `Loader` is a full-screen MUI `Dialog` whose
 * backdrop `ModalBox.styles.js` paints `rgba(0, 0, 0, 0.3)`, so the spinner
 * ring sits on `#a4a5a5` and not on the page ground. Measured against that:
 * `grey.700` 1.83:1, `grey.800` 3.98:1, `grey.900` 5.30:1, and today's purple
 * 2.76:1. The WCAG floor for a non-text graphic is 3:1, which the purple
 * already misses — pick a lighter grey here and the boot ring comes out dimmer
 * than the defect it replaces.
 *
 * **No `background` key, and the omission is load-bearing.** `getTheme`
 * (`lib/ui/theme/themeUtils.js`) selects `YaloDarkPalette` for dark mode and
 * then spreads the flat palette over it, so a `background` written here would
 * paint a dark-mode storefront white. Leaving it out keeps the library's own
 * `#F7F9F9` / `#201F39` pair, which is already neutral in both modes.
 */
export const NEUTRAL_BOOT_PALETTE: NonNullable<ICustomTheme['palette']> = {
  primary: { main: '#2E3133', contrastText: '#FFF' },
  secondary: { main: '#EEF1F3', contrastText: '#2E3133' },
}

/**
 * Puts {@link NEUTRAL_BOOT_PALETTE} underneath whatever palette the caller has,
 * so a storefront that authors `palette.primary` is untouched and one that
 * authors nothing renders grey rather than Yalo purple.
 *
 * The merge is one level deep, matching `combinePalettes` in the library: a
 * storefront naming `primary` replaces the whole object, `contrastText`
 * included, and MUI recomputes that against `contrastThreshold: 3`.
 * `palette.mode` rides through untouched, so a dark storefront still gets dark.
 *
 * Applies to the resolved config as well as the boot window. That is
 * deliberate: gating it on `config === undefined` would hand a themeless
 * storefront a grey-to-purple transition where it has none today.
 */
export const resolveAppTheme = (configTheme?: ICustomTheme): ICustomTheme => ({
  ...configTheme,
  palette: { ...NEUTRAL_BOOT_PALETTE, ...configTheme?.palette },
})

/**
 * Every palette member a storefront can author, taken from `ThemePalette` in
 * `commerce-scs/cinnamon/theme.go`. That struct is the authority on what comes
 * off the wire, so anything outside this list reached `localStorage` by
 * corruption or by an attacker and is dropped on read and on write.
 */
export const CACHEABLE_PALETTE_KEYS = [
  'mode',
  'background',
  'text',
  'primary',
  'secondary',
  'error',
  'warning',
  'info',
  'success',
] as const

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** A palette member (`primary`, `background`, …) is an object of CSS strings. */
const sanitizeMember = (value: unknown): Record<string, string> | undefined => {
  if (!isPlainObject(value)) {
    return undefined
  }
  const member: Record<string, string> = {}
  Object.entries(value).forEach(([key, entry]) => {
    if (typeof entry === 'string') {
      member[key] = entry
    }
  })
  return Object.keys(member).length > 0 ? member : undefined
}

/**
 * Narrows an arbitrary value to the nine palette members SCS can author.
 * Returns `undefined` when nothing survives, so an empty result reads as
 * "no cached palette" rather than as an empty one.
 *
 * This is not tidiness. Anything that achieves XSS on the origin can write
 * `localStorage`, the value reaches `createTheme` during render, and a
 * malformed `palette.primary` throws there — a white screen with no route out.
 * The allowlist also bounds the payload: `typography`, `components` and
 * `shadows` are not cached, so a returning shopper gets the brand colours and
 * the library's default type metrics until the config lands.
 */
export const sanitizePalette = (
  value: unknown,
): NonNullable<ICustomTheme['palette']> | undefined => {
  if (!isPlainObject(value)) {
    return undefined
  }
  const palette: Record<string, unknown> = {}
  CACHEABLE_PALETTE_KEYS.forEach((key) => {
    const member = value[key]
    if (key === 'mode') {
      if (member === 'light' || member === 'dark') {
        palette.mode = member
      }
      return
    }
    const sanitized = sanitizeMember(member)
    if (sanitized) {
      palette[key] = sanitized
    }
  })
  return Object.keys(palette).length > 0
    ? (palette as NonNullable<ICustomTheme['palette']>)
    : undefined
}

export const bootThemeStorageKey = (slug: string): string =>
  `cinnamon.bootTheme.${slug}`

/**
 * The last palette this browser saw for `slug`, or `undefined`.
 *
 * `cinnamon/src` uses no browser storage anywhere else — the only precedent in
 * the repo is `packages/host/src/utils.ts`, and it is unguarded. Every access
 * here sits in a `try`/`catch`: a private window, cleared site data, or a
 * webview with storage disabled has to degrade to the neutral palette rather
 * than throw during render.
 */
export const readCachedTheme = (slug?: string): ICustomTheme | undefined => {
  if (!slug) {
    return undefined
  }
  try {
    const raw = window.localStorage.getItem(bootThemeStorageKey(slug))
    if (!raw) {
      return undefined
    }
    const palette = sanitizePalette(JSON.parse(raw))
    return palette ? { palette } : undefined
  } catch {
    return undefined
  }
}

/**
 * Records the resolved palette for `slug` so the next cold open paints branded
 * on the first frame.
 *
 * A storefront whose theme sanitizes to nothing clears the key instead of
 * writing an empty object, so dropping a theme in SCS also drops the cached one
 * rather than leaving the old colours to outlive it.
 *
 * Swallows a throwing `setItem`: a quota error must not fail `fetchConfig`.
 */
export const writeCachedTheme = (
  slug: string | undefined,
  theme: ICustomTheme | undefined,
): void => {
  if (!slug) {
    return
  }
  try {
    const palette = sanitizePalette(theme?.palette)
    if (!palette) {
      window.localStorage.removeItem(bootThemeStorageKey(slug))
      return
    }
    window.localStorage.setItem(
      bootThemeStorageKey(slug),
      JSON.stringify(palette),
    )
  } catch {
    // Storage is a nicety here; the neutral palette is always a correct answer.
  }
}

export default resolveAppTheme
